import type { ElementType, JSX, MouseEvent, ReactNode } from 'react';
import { useId } from 'react';
import { historyStepsBack } from './history-memory';
import { cx } from './primitives';

/** Where the arrow leads when nothing in this tab's history is behind the page. */
export interface PageHeadingBack {
  readonly href: string;
  readonly label: string;
  /**
   * The path prefix every page of this record shares, when its tabs are routes
   * (`/organizations/bakeries/menu/<id>`). Omitted, only entries at this exact
   * path are stepped over.
   */
  readonly scope?: string;
}

/**
 * Back to where the reader WAS, not to the list's front page (founder,
 * 2026-09-13). A plain click with an in-app page behind this one walks history
 * there, which is what brings its filters and scroll back with it; otherwise -
 * a deep link, a modified click that opens a new tab - the link's own `href`
 * runs as it always did.
 */
function backClick(back: PageHeadingBack) {
  return (event: MouseEvent): void => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    const steps = historyStepsBack(back.scope);
    if (steps === 0) return;
    event.preventDefault();
    history.go(-steps);
  };
}

/**
 * One page heading, for every portal (founder, 2026-09-06).
 *
 * Four decisions live here, and each of them was two implementations before it
 * was one:
 *
 * 1. THE WAY BACK IS AN ARROW AT THE LEFT OF THE TITLE, never a worded link
 *    under it and never a button among the actions on the right - that is where
 *    a page's PRIMARY actions live, and a way out is not one of those. It is the
 *    SAME arrow everywhere: a bare chevron, drawn once, below. The internal
 *    portal drew a chevron on its product page and the partner portal drew a
 *    long arrow with a shaft on its record pages, so "the back arrow" named two
 *    different marks depending on which portal you were looking at.
 *
 * 2. ONE SIZE FOR EVERY TITLE: `--cib-text-title`, the size the product page
 *    already used. The two portals' own headings were `--cib-text-display` (30px)
 *    while the product page was 20px, so the same reader met two title scales a
 *    click apart. A record page has to fit on a screen without scrolling, and a
 *    display-sized heading over a way back was the tallest thing on it saying
 *    the least.
 *
 * 3. A SUB-TAB THAT ALREADY NAMES THE SCREEN IS THE HEADING. The sub-navigation
 *    strips are built from the screens' own title keys precisely so the strip and
 *    the heading cannot drift, which meant every one of those screens drew the
 *    same words twice, a few pixels apart. `titleInTab` keeps the `<h1>` in the
 *    document - a reader who cannot see which tab is lit still gets the page's
 *    name - and stops drawing it.
 *
 * 4. THE LINE OF PURPOSE GOES BEHIND AN (i). It is read once, on the first visit,
 *    and then costs a line of vertical space on every visit after that. Hovering
 *    the mark shows it; nothing is drawn until then.
 *
 * Every string is passed in already translated (PRD NFR-006).
 */
export interface PageHeadingProps {
  /** The screen's name, or the record's own name. Never "Edit <name>". */
  title: ReactNode;
  /**
   * True when the active sub-tab above already says this. The heading stays in
   * the document, for a reader who is not looking at the strip, and is not drawn.
   */
  titleInTab?: boolean;
  /**
   * The one line of purpose, already translated. Shown on hovering the (i)
   * beside the title, and never drawn as prose.
   */
  info?: string;
  /** Accessible name of the (i) itself, e.g. "About this screen". Required with `info`. */
  infoLabel?: string;
  /**
   * The way back. `href` is the list this record belongs to, and it is only the
   * FALLBACK: when this tab reached the page from another page of the app, the
   * arrow returns there instead, with its filters and scroll as the reader left
   * them (`history-memory.ts`). `scope` is the path prefix the record's own
   * routed tabs share, so they are stepped over rather than walked back through.
   * Already translated.
   */
  back?: PageHeadingBack;
  /**
   * The app's router link. A portal passes its own (`wouter`'s `Link`), so the
   * arrow is a route change rather than a full page load. A plain anchor
   * otherwise, which is what `renderToStaticMarkup` gets.
   */
  linkAs?: ElementType;
  /** The page's primary actions, at the right edge of the heading row. */
  actions?: ReactNode;
  /**
   * A rare non-prose line that belongs beside the title rather than behind the
   * (i) - the catalogue's link to the house whose shelf is on screen. Prose is
   * `info`; this is for a line the reader has to be able to CLICK, which a
   * tooltip cannot offer.
   */
  lead?: ReactNode;
}

export function PageHeading({
  title,
  titleInTab = false,
  info,
  infoLabel,
  back,
  linkAs,
  actions,
  lead,
}: PageHeadingProps): JSX.Element {
  const Link: ElementType = linkAs ?? 'a';
  return (
    <header className="cib-pageheading">
      <div className="cib-pageheading__lead">
        {back !== undefined ? (
          <Link
            href={back.href}
            className="cib-pageheading__back"
            aria-label={back.label}
            title={back.label}
            onClick={backClick(back)}
          >
            <BackChevron />
          </Link>
        ) : null}
        <h1 className={cx('cib-pageheading__title', titleInTab && 'cib-visually-hidden')}>
          {title}
        </h1>
        {info !== undefined && infoLabel !== undefined ? (
          <InfoHint label={infoLabel} text={info} />
        ) : null}
        {lead !== undefined ? <div className="cib-pageheading__note">{lead}</div> : null}
      </div>
      {actions !== undefined ? <div className="cib-pageheading__actions">{actions}</div> : null}
    </header>
  );
}

/**
 * THE back arrow: a bare chevron, at the weight of the title beside it.
 *
 * Drawn rather than imported - packages/ui carries no icon set - and drawn ONCE,
 * which is the whole point of it living here. It is not a control: no box, no
 * border, no height of its own, because a 36px button beside a 20px title reads
 * as the loudest thing on the row.
 *
 * Exported for the one heading that is not a `PageHeading`: the guest vitrine
 * sets a house's name at its own, larger title size, and its way back to the
 * chooser has to be THIS mark rather than a second drawing of it.
 */
export function BackChevron(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 4 6 10l6 6" />
    </svg>
  );
}

/**
 * The (i) that holds a screen's line of purpose.
 *
 * A real `<button>` and not a bare glyph: it has to be reachable without a mouse,
 * and on a touch screen there is no hover at all - a tap focuses the button and
 * `:focus-within` shows the same bubble. The text is the button's
 * `aria-describedby`, so a screen reader gets it whether or not the bubble is
 * drawn, and `title` is deliberately absent: the browser's own tooltip would
 * appear a second later, on top of ours, saying the same thing twice.
 */
function InfoHint({ label, text }: { label: string; text: string }): JSX.Element {
  const id = useId();
  return (
    <span className="cib-hint">
      <button type="button" className="cib-hint__mark" aria-label={label} aria-describedby={id}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="10" cy="10" r="7.5" />
          <path d="M10 9v4.5" />
          <path d="M10 6.4h.01" />
        </svg>
      </button>
      <span className="cib-hint__bubble" id={id} role="tooltip">
        {text}
      </span>
    </span>
  );
}
